import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { useStore } from 'src/stores';
const CELL_BORDER = '1px solid #e0e0e0';
const LABEL_WIDTH = 300;
const parseCsvFile = async file => {
  try {
    const text = await file.text();
    const lines = text.split('\n').filter(l => l.trim());
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').trim()).filter(Boolean);
    const rows = lines.slice(1).map(line => line.split(',').map(v => v.trim().replace(/^"|"$/g, '').trim()));
    return {
      headers,
      rows
    };
  } catch (_unused) {
    return {
      headers: [],
      rows: []
    };
  }
};
const getColumnOptions = (rows, headers, column) => {
  const colIdx = headers.indexOf(column);
  if (colIdx === -1) return [];
  return [...new Set(rows.map(row => row[colIdx]).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, {
    numeric: true
  }));
};

// Module-level memoized row — only re-renders when its own props actually change
const UnitMeterIdRow = /*#__PURE__*/React.memo(_ref => {
  let unit = _ref.unit,
    idx = _ref.idx,
    unitCount = _ref.unitCount,
    ids = _ref.ids,
    csvMeterOptions = _ref.csvMeterOptions,
    filteredOptions = _ref.filteredOptions,
    manualFlags = _ref.manualFlags,
    onAdd = _ref.onAdd,
    onRemove = _ref.onRemove,
    onUpdate = _ref.onUpdate,
    onToggleManual = _ref.onToggleManual;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'flex-start',
      borderBottom: idx < unitCount - 1 ? CELL_BORDER : undefined
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: LABEL_WIDTH,
      flexShrink: 0,
      padding: '12px 12px 12px 0'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, unit.label)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '4px 0 4px 12px',
      borderLeft: CELL_BORDER
    }
  }, ids.map((val, i) => {
    var _filteredOptions$i;
    const showSelect = csvMeterOptions.length > 0 && !manualFlags[i];
    const slotOptions = filteredOptions ? (_filteredOptions$i = filteredOptions[i]) !== null && _filteredOptions$i !== void 0 ? _filteredOptions$i : csvMeterOptions : csvMeterOptions;
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      style: {
        marginBottom: i < ids.length - 1 ? 4 : 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }
    }, showSelect ? /*#__PURE__*/React.createElement(FormControl, {
      size: "small",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Select, {
      displayEmpty: true,
      value: val,
      onChange: e => onUpdate(unit.id, i, e.target.value),
      renderValue: v => v ? v : /*#__PURE__*/React.createElement("span", {
        style: {
          color: '#9e9e9e'
        }
      }, "Select meter ID")
    }, slotOptions.map(opt => /*#__PURE__*/React.createElement(MenuItem, {
      key: opt,
      value: opt
    }, opt)))) : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "123456789",
      value: val,
      onChange: e => onUpdate(unit.id, i, e.target.value)
    }), i === 0 ? /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      size: "small",
      style: {
        minWidth: 32,
        flexShrink: 0,
        padding: '5px 8px'
      },
      onClick: () => onAdd(unit.id)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    })) : /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      size: "small",
      style: {
        minWidth: 32,
        flexShrink: 0,
        padding: '5px 8px'
      },
      onClick: () => onRemove(unit.id, i)
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    }))), showSelect && i > 0 && i === ids.length - 1 && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "primary",
      style: {
        cursor: 'pointer',
        marginTop: 2,
        fontSize: 12
      },
      onClick: () => onToggleManual(unit.id, i)
    }, "Enter manually"));
  })));
});

// Defined at module level — never remounts due to parent re-renders
const ProvideUnitMeterIDsFormContent = _ref2 => {
  let dataRef = _ref2.dataRef,
    unitList = _ref2.unitList,
    initialMeterIds = _ref2.initialMeterIds,
    initialPropertyMeterId = _ref2.initialPropertyMeterId;
  const _useState = useState(initialPropertyMeterId || ''),
    _useState2 = _slicedToArray(_useState, 2),
    propertyMeterId = _useState2[0],
    setPropertyMeterId = _useState2[1];
  const _useState3 = useState(() => {
      const init = {};
      unitList.forEach(unit => {
        const vals = initialMeterIds === null || initialMeterIds === void 0 ? void 0 : initialMeterIds[unit.id];
        init[unit.id] = Array.isArray(vals) && vals.length ? vals : [''];
      });
      return init;
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    unitMeterIds = _useState4[0],
    setUnitMeterIds = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    csvFile = _useState6[0],
    setCsvFile = _useState6[1];
  const _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    csvHeaders = _useState8[0],
    setCsvHeaders = _useState8[1];
  const _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    csvRows = _useState0[0],
    setCsvRows = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    meterIdColumn = _useState10[0],
    setMeterIdColumn = _useState10[1];
  const _useState11 = useState([]),
    _useState12 = _slicedToArray(_useState11, 2),
    csvMeterOptions = _useState12[0],
    setCsvMeterOptions = _useState12[1];
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isDragging = _useState14[0],
    setIsDragging = _useState14[1];
  const _useState15 = useState({}),
    _useState16 = _slicedToArray(_useState15, 2),
    manualInputs = _useState16[0],
    setManualInputs = _useState16[1];
  const fileInputRef = useRef(null);

  // Keeps previous filtered arrays stable when content hasn't changed,
  // so React.memo on rows skips re-renders for unaffected units
  const filteredOptionsCache = useRef({});
  if (dataRef) {
    dataRef.current = {
      unitMeterIds,
      propertyMeterId
    };
  }

  // Stable handlers — never recreated, use functional setState
  const addMeterId = useCallback(unitId => {
    setUnitMeterIds(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [unitId]: [...(prev[unitId] || ['']), '']
    }));
  }, []);
  const removeMeterId = useCallback((unitId, index) => {
    setUnitMeterIds(prev => {
      const arr = [...(prev[unitId] || [''])];
      arr.splice(index, 1);
      return _objectSpread(_objectSpread({}, prev), {}, {
        [unitId]: arr.length ? arr : ['']
      });
    });
  }, []);
  const updateMeterId = useCallback((unitId, index, value) => {
    setUnitMeterIds(prev => {
      const arr = [...(prev[unitId] || [''])];
      arr[index] = value;
      return _objectSpread(_objectSpread({}, prev), {}, {
        [unitId]: arr
      });
    });
  }, []);
  const toggleManualInput = useCallback((unitId, index) => {
    const key = "".concat(unitId, "_").concat(index);
    setManualInputs(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [key]: true
    }));
    setUnitMeterIds(prev => {
      const arr = [...(prev[unitId] || [''])];
      arr[index] = '';
      return _objectSpread(_objectSpread({}, prev), {}, {
        [unitId]: arr
      });
    });
  }, []);
  const buildEmptyIds = useCallback(() => {
    const reset = {};
    unitList.forEach(unit => {
      reset[unit.id] = [''];
    });
    return reset;
  }, [unitList]);
  const handleFileSelect = useCallback(async file => {
    const _await$parseCsvFile = await parseCsvFile(file),
      headers = _await$parseCsvFile.headers,
      rows = _await$parseCsvFile.rows;
    setCsvFile(file);
    setCsvHeaders(headers);
    setCsvRows(rows);
    setMeterIdColumn('');
    setCsvMeterOptions([]);
    filteredOptionsCache.current = {};
    setManualInputs({});
    setUnitMeterIds(buildEmptyIds());
  }, [buildEmptyIds]);
  const handleFileInputChange = useCallback(async e => {
    var _e$target$files;
    const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) await handleFileSelect(file);
  }, [handleFileSelect]);
  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback(() => setIsDragging(false), []);
  const handleDrop = useCallback(async e => {
    var _e$dataTransfer$files;
    e.preventDefault();
    setIsDragging(false);
    const file = (_e$dataTransfer$files = e.dataTransfer.files) === null || _e$dataTransfer$files === void 0 ? void 0 : _e$dataTransfer$files[0];
    if (file) await handleFileSelect(file);
  }, [handleFileSelect]);
  const handleRemoveFile = useCallback(() => {
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setMeterIdColumn('');
    setCsvMeterOptions([]);
    filteredOptionsCache.current = {};
    setManualInputs({});
    setUnitMeterIds(buildEmptyIds());
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [buildEmptyIds]);
  const handleColumnChange = useCallback(column => {
    setMeterIdColumn(column);
    setCsvMeterOptions(getColumnOptions(csvRows, csvHeaders, column));
    filteredOptionsCache.current = {};
    setManualInputs({});
    setUnitMeterIds(buildEmptyIds());
  }, [csvRows, csvHeaders, buildEmptyIds]);

  // Per-slot filtered options — for each select within a unit, exclude values
  // already selected in any other slot (same unit or other units)
  const filteredOptionsMap = useMemo(() => {
    if (!csvMeterOptions.length) return {};
    const allSelected = new Set(unitList.flatMap(u => (unitMeterIds[u.id] || ['']).filter(Boolean)));
    const newMap = {};
    unitList.forEach(unit => {
      const ids = unitMeterIds[unit.id] || [''];
      const perSlot = ids.map(slotVal => {
        return csvMeterOptions.filter(opt => !allSelected.has(opt) || opt === slotVal && slotVal);
      });
      newMap[unit.id] = perSlot;
    });
    filteredOptionsCache.current = newMap;
    return newMap;
  }, [unitMeterIds, unitList, csvMeterOptions]);
  const manualFlagsMap = useMemo(() => {
    const map = {};
    unitList.forEach(unit => {
      const ids = unitMeterIds[unit.id] || [''];
      map[unit.id] = ids.map((_, i) => !!manualInputs["".concat(unit.id, "_").concat(i)]);
    });
    return map;
  }, [unitList, unitMeterIds, manualInputs]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      marginBottom: 24
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600,
      width: LABEL_WIDTH + 1,
      flexShrink: 0,
      borderRight: CELL_BORDER,
      padding: '8px 0'
    }
  }, "Property meter ID"), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    fullWidth: true,
    placeholder: "123456789",
    value: propertyMeterId,
    onChange: e => setPropertyMeterId(e.target.value)
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      border: "1px dashed ".concat(isDragging ? '#1976d2' : '#b8c4ce'),
      borderRadius: 4,
      padding: csvFile ? 12 : 32,
      cursor: !csvFile ? 'pointer' : 'default',
      marginBottom: 16,
      backgroundColor: isDragging ? 'rgba(25,118,210,0.04)' : undefined
    },
    onClick: () => {
      var _fileInputRef$current;
      return !csvFile && ((_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click());
    },
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  }, !csvFile ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "light"
  }, "Upload a CSV to auto-fill meter IDs (optional)") : /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 600
    }
  }, csvFile.name), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    size: "small",
    style: {
      minWidth: 0
    },
    onClick: e => {
      e.stopPropagation();
      handleRemoveFile();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    onChange: handleFileInputChange
  })), csvHeaders.length > 0 && /*#__PURE__*/React.createElement(FormControl, {
    size: "small",
    fullWidth: true,
    style: {
      marginBottom: 16
    }
  }, /*#__PURE__*/React.createElement(Select, {
    displayEmpty: true,
    value: meterIdColumn,
    onChange: e => handleColumnChange(e.target.value),
    renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
      style: {
        color: '#9e9e9e'
      }
    }, "Select meter ID column")
  }, csvHeaders.map(h => /*#__PURE__*/React.createElement(MenuItem, {
    key: h,
    value: h
  }, h)))), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      width: LABEL_WIDTH,
      flexShrink: 0,
      padding: '12px 12px 12px 0'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Unit")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '4px 0 4px 12px',
      borderLeft: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Meter ID"))), unitList.map((unit, idx) => {
    var _filteredOptionsMap$u;
    return /*#__PURE__*/React.createElement(UnitMeterIdRow, {
      key: unit.id,
      unit: unit,
      idx: idx,
      unitCount: unitList.length,
      ids: unitMeterIds[unit.id] || [''],
      csvMeterOptions: csvMeterOptions,
      filteredOptions: (_filteredOptionsMap$u = filteredOptionsMap[unit.id]) !== null && _filteredOptionsMap$u !== void 0 ? _filteredOptionsMap$u : null,
      manualFlags: manualFlagsMap[unit.id] || [],
      onAdd: addMeterId,
      onRemove: removeMeterId,
      onUpdate: updateMeterId,
      onToggleManual: toggleManualInput
    });
  }));
};
export const ProvideUnitMeterIDsModal = _ref3 => {
  var _store$homeDashboardS, _store$homeDashboardS2, _utility$provider;
  let utility = _ref3.utility,
    propertyUtilityId = _ref3.propertyUtilityId,
    hasMeterIds = _ref3.hasMeterIds,
    onSave = _ref3.onSave,
    onHasMeterIdsChange = _ref3.onHasMeterIdsChange;
  const store = useStore();
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isOpen = _useState18[0],
    setIsOpen = _useState18[1];
  const _useState19 = useState(0),
    _useState20 = _slicedToArray(_useState19, 2),
    formKey = _useState20[0],
    setFormKey = _useState20[1];
  const _useState21 = useState({}),
    _useState22 = _slicedToArray(_useState21, 2),
    initialMeterIds = _useState22[0],
    setInitialMeterIds = _useState22[1];
  const _useState23 = useState(''),
    _useState24 = _slicedToArray(_useState23, 2),
    initialPropertyMeterId = _useState24[0],
    setInitialPropertyMeterId = _useState24[1];
  const _useState25 = useState(false),
    _useState26 = _slicedToArray(_useState25, 2),
    deleteConfirmOpen = _useState26[0],
    setDeleteConfirmOpen = _useState26[1];
  const dataRef = useRef({});
  const storeUnits = (_store$homeDashboardS = (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.selectedPropertyDetails()) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.details) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.unitSetup) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.units) !== null && _store$homeDashboardS !== void 0 ? _store$homeDashboardS : [];
  const unitList = storeUnits.filter(u => u.id && u.name).sort((a, b) => a.name.localeCompare(b.name, undefined, {
    numeric: true
  })).map(u => ({
    id: u.id,
    label: u.name
  }));
  const subtitle = [utility === null || utility === void 0 ? void 0 : utility.utilityType, utility === null || utility === void 0 || (_utility$provider = utility.provider) === null || _utility$provider === void 0 ? void 0 : _utility$provider.utilityProviderName].filter(Boolean).join(', ');
  const handleOpen = async () => {
    let meterIds = {};
    let propertyMeterId = '';
    if (propertyUtilityId) {
      const resp = await store.allocations.getSubmeterBillingUnitMeterMapsAsync.run({
        propertyUtilityId
      });
      if (resp !== null && resp !== void 0 && resp.isSuccessful && Array.isArray(resp.response)) {
        resp.response.forEach(entry => {
          if (!propertyMeterId && (entry.propertyMeterId || entry.propertyUtilityMeterId)) {
            propertyMeterId = entry.propertyMeterId || entry.propertyUtilityMeterId;
          }
          if (entry.unitId && entry.meterId) {
            if (!meterIds[entry.unitId]) meterIds[entry.unitId] = [];
            meterIds[entry.unitId].push(entry.meterId);
          }
        });
      }
    }
    setInitialMeterIds(meterIds);
    setInitialPropertyMeterId(propertyMeterId);
    setFormKey(k => k + 1);
    setIsOpen(true);
  };
  const handleClose = () => setIsOpen(false);
  const handleSave = async () => {
    const _dataRef$current = dataRef.current,
      unitMeterIds = _dataRef$current.unitMeterIds,
      propertyMeterId = _dataRef$current.propertyMeterId;
    let uniqueUnitCount = 0;
    if (propertyUtilityId) {
      var _store$homeDashboardS3;
      const propertyId = (_store$homeDashboardS3 = store.homeDashboardStore.getCurrentProperty) === null || _store$homeDashboardS3 === void 0 ? void 0 : _store$homeDashboardS3.id;
      const maps = [];
      unitList.forEach(unit => {
        ((unitMeterIds === null || unitMeterIds === void 0 ? void 0 : unitMeterIds[unit.id]) || []).filter(Boolean).forEach(meterId => {
          maps.push({
            propertyId,
            propertyUtilityId,
            propertyMeterId: propertyMeterId || '',
            unitId: unit.id,
            meterId
          });
        });
      });
      uniqueUnitCount = new Set(maps.map(m => m.unitId)).size;
      if (hasMeterIds) {
        const resp = await store.allocations.postSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.run({
          propertyUtilityId,
          maps
        });
        if (resp !== null && resp !== void 0 && resp.isSuccessful) toast('success', {
          body: ['Meter IDs updated!']
        });
      } else {
        const resp = await store.allocations.postSubmeterBillingUnitMeterMapsBatchAsync.run({
          maps
        });
        if (resp !== null && resp !== void 0 && resp.isSuccessful) toast('success', {
          body: ['Meter IDs saved!']
        });
      }
    }
    onHasMeterIdsChange === null || onHasMeterIdsChange === void 0 || onHasMeterIdsChange(uniqueUnitCount);
    onSave === null || onSave === void 0 || onSave(dataRef.current);
    handleClose();
  };
  const handleDeleteConfirm = async () => {
    const resp = await store.allocations.deleteSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.run({
      propertyUtilityId
    });
    if (resp !== null && resp !== void 0 && resp.isSuccessful) toast('success', {
      body: ['Meter IDs deleted!']
    });
    setDeleteConfirmOpen(false);
    onHasMeterIdsChange === null || onHasMeterIdsChange === void 0 || onHasMeterIdsChange(0);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: hasMeterIds ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: 'flex',
        alignItems: 'center'
      }
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: handleOpen
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Edit")), /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: e => {
        e.stopPropagation();
        setDeleteConfirmOpen(true);
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "trash"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Delete"))) : /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      size: "small",
      onClick: handleOpen
    }, "Provide"),
    title: /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Provide unit meter IDs"),
    subTitle: subtitle ? /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light"
    }, subtitle) : undefined,
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleSave,
      disabled: store.allocations.postSubmeterBillingUnitMeterMapsBatchAsync.inProgress || store.allocations.postSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.inProgress
    }, "Save meter IDs"),
    isOpen: isOpen,
    handleOpen: handleOpen,
    handleClose: handleClose
  }, /*#__PURE__*/React.createElement(ProvideUnitMeterIDsFormContent, {
    key: formKey,
    dataRef: dataRef,
    unitList: unitList,
    initialMeterIds: initialMeterIds,
    initialPropertyMeterId: initialPropertyMeterId
  })), /*#__PURE__*/React.createElement(Dialog, {
    open: deleteConfirmOpen,
    onClose: () => setDeleteConfirmOpen(false)
  }, /*#__PURE__*/React.createElement(DialogTitle, null, "Delete meter ID configuration"), /*#__PURE__*/React.createElement(DialogContent, null, /*#__PURE__*/React.createElement(DialogContentText, null, "Are you sure you want to delete all meter ID mappings for this utility? This action cannot be undone.")), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    onClick: () => setDeleteConfirmOpen(false)
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: handleDeleteConfirm,
    disabled: store.allocations.deleteSubmeterBillingUnitMeterMapsByPropertyUtilityAsync.inProgress
  }, "Delete"))));
};